/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Database} from "../APIClient.jsx";
import {UI} from "../UIGenerator.jsx";
import ElementStatus from "./ElementStatus.jsx";
import {Socket} from "../Socket.js";
import {useSub} from "subscription";
import ListButton from "./BasicInputs/Buttons/ListButton.jsx";
import SmallSearch from "./SmallSearch.jsx";
import Algo from "toolbox/algo";
import {motion} from "motion/react";
import {SidebarNavItem} from "./SidebarNavItem.jsx";
import {CatalogAPI} from "../APIs/CatalogApi.js";
import MainMenuActionBar from "./MainMenu/MainMenuActionBar.jsx";
import {useRegisterShortcut} from "../hooks/useShortcuts.jsx";


const elementKey = (element) => element.type + "_" + element.indexKey;

const MainMenu = () => {
    const [catalogItems, setCatalogItems] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [currentPath, setCurrentPath] = useState(UI.page().getCurrentPath());
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());

    useEffect(() => {
        const fetchData = async () => {
            let catalogData = await CatalogAPI.list();
            setCatalogItems(catalogData?.items ?? []);
        };
        fetchData();
    }, []);

    useSub(() => Database.table("Catalog", "app").onChange((change) => {
        setCatalogItems(prev => Socket.applyChange(prev, change))
    }, []), "liveCatalogListData");

    useSub(() => UI.page().onPageChange(newPath => {
        setCurrentPath(prev => {
            if (newPath !== prev) return newPath;
            return prev;
        })
    }));


    const getVisibleElements = () => {
        const items = catalogItems.map(item => ({
            ...item,
            type: 'Catalog',
            name: item.name,
            path: "/ci/" + item.indexKey,
            icon: item.type === "page" ? "BsWindow" : "Container"
        }));

        if (!searchInput) return items;

        return items
            .map(item => ({
                ...item,
                _score: Algo.levenshteinDistance(item.name, searchInput)
            }))
            .sort((a, b) => a._score - b._score)
            .slice(0, 4);
    };

    const visibleElements = getVisibleElements();

    const selectedElements = useMemo(() => {
        return visibleElements.filter(element => selectedKeys.has(elementKey(element)));
    }, [visibleElements, selectedKeys]);


    useRegisterShortcut("Close Select", "General", ["esc"], () => {
        setSelectedKeys(new Set());
    });

    const clearSelection = () => setSelectedKeys(new Set());

    const handleDeleteAll = async () => {
        await Promise.all(selectedElements.map(element => CatalogAPI.remove(element.indexKey)));
        clearSelection();
    };

    const handleOpenAllInTabs = () => {
        for (const element of selectedElements) {
            window.open(element.path, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <>
            <div className={" flex flex-col mb-2"}>
                <SmallSearch
                    placeholder="Search Elements"
                    value={searchInput}
                    onChange={(value) => setSearchInput(value)}
                >
                    <div className="flex items-center justify-center h-8">
                        <h2 className="text-sm font-medium text-gray-700 ml-1 grow">Elements</h2>
                    </div>
                </SmallSearch>
            </div>
            <motion.div layout={"position"} role="listCommand" className="flex flex-1 flex-col gap-y-1.5">


                {visibleElements.map(element => {
                    let isSelected = currentPath === element.path
                    const key = elementKey(element)
                    const isMultiSelected = selectedKeys.has(key)

                    return (<SidebarNavItem
                        text={element.name}
                        key={key}
                        ownId={element.indexKey}
                        isOpen={isSelected}
                        isMultiSelected={isMultiSelected}
                        iconName={element.icon}
                        link={"navItem.Link"}
                        onMiddleMouseClick={() => {
                            window.open(element.path, '_blank', 'noopener,noreferrer')
                        }}
                        onClick={(event) => {
                            if (event.shiftKey) {
                                setSelectedKeys(prev => {
                                    const next = new Set(prev);
                                    if (next.has(key)) next.delete(key);
                                    else next.add(key);
                                    return next;
                                });
                            } else {
                                UI.page().navigate({
                                    pathname: element.path
                                })
                            }

                        }}
                        status={<>
                            <ElementStatus name={element.type} elementId={element.indexKey}/>

                        </>}

                    />)
                })}

                <div className={"mt-2"}>
                    <ListButton text={"New Deployment"}
                                onClick={() => {
                                    UI.page().navigate({
                                        pathname: "/new"
                                    })
                                }}
                    />
                </div>
            </motion.div>

            <MainMenuActionBar
                selectedElements={selectedElements}
                onClear={clearSelection}
                onDeleteAll={handleDeleteAll}
                onOpenAllInTabs={handleOpenAllInTabs}
            />

        </>
    );
}

MainMenu.propTypes = {};

MainMenu.defaultProps = {};


export default MainMenu;